'use client';

/**
 * VOX - stavovy model dialogu a nepretrzity hlasovy rozhovor.
 *
 * Kriteria su JEDEN objekt, ktory zije cely rozhovor:
 *  - kazda veta sa do neho ZLUCUJE, neprepisuje ho
 *  - null v patchi kriterium ODOBERIE
 *  - kazda zmena spusti nove vyhladavanie s debounce, nie na kazde slovo
 *  - kriteria sa daju opravit aj rukou, nielen hlasom
 *
 * Hlasovy rezim je stavovy automat:
 *
 *   idle --start--> speaking(pozdrav) --> listening --(ticho po reci)--> thinking
 *     ^                                      |                             |
 *     |                                      | 20 s bez reci               v
 *     +----------- stop() <------------------+                  speaking(odpoved)
 *                                                                          |
 *                                            listening <-------------------+
 *
 * Tri veci, ktore rozhoduju o tom, ci to funguje:
 *
 * 1) Krajenie reci. Mikrofonovy stream sa otvara RAZ. Nahravac sa pri kazdej
 *    pauze zastavi a hned znova naskrtne nad tym istym streamom - webm potrebuje
 *    vlastnu hlavicku, takze stream sa neda rezat uprostred. Povolenie sa
 *    druhykrat nepyta.
 *
 * 2) Vlastny hlas. Kym VOX hovori, nahravanie je zastavene - inak by si svoju
 *    vetu nahral, prepisal a nastavil podla nej filter.
 *
 * 3) Ticho k modelu neposielame. Segment bez dostatocnej hlasitosti sa zahodi
 *    na klientovi - STT si na tichu vymysla cele vety (viz transcript-guard.js).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { VOX_API, type VoxCriteria, type VoxListing, type VoxMessage, type VoxRelax, type VoxSettings } from './coreTypes';

const DEBOUNCE_MS = 320;

/** Pocet budov v zvukovej vlne. */
const BARS = 28;

/** Hlasitost, nad ktorou povazujeme zvuk za rec. */
const SPEECH_ON = 0.075;
/** Ako dlho musi byt ticho, aby sa veta povazovala za dokoncenu. */
const SILENCE_MS = 900;
/** Ako dlho musi byt zvuk nad prahom, aby sa povazoval za rec a nie za ranu. */
const SPEECH_MIN_MS = 180;
/** Minimalna dlzka segmentu - kratsie byva kaslanie alebo klik. */
const MIN_SEGMENT_MS = 500;
/** Ked clovek nepovie nic tak dlho, rozhovor sa ukonci sam. */
const IDLE_END_MS = 20000;

const GREETING = 'Zdravím, som VOX. Čo hľadáte?';

/** Zaloha, kym sa nacita nastavenie zo servera. */
const FALLBACK = {
    silenceMs: SILENCE_MS,
    vadLevel: SPEECH_ON,
    idleEndMs: IDLE_END_MS,
    greeting: GREETING,
    startPhrase: 'Poďme na to.',
};

export type VoxMode = 'idle' | 'listening' | 'thinking' | 'speaking';

/** DOCASNE: posiela stav VAD na server, aby sa dalo dohladat, kde sa to zasekne. */
function vlog(event: string, data?: Record<string, unknown>) {
    try {
        console.log('[vox]', event, data || {});
        void fetch(VOX_API + '/log', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ event, data: data || {} }),
        });
    } catch {
        /* diagnostika nesmie nic rozbit */
    }
}

function isEmpty(v: unknown) {
    return jePrazdne(v);
}

function jePrazdne(v: unknown): boolean {
    if (v === null || v === undefined) return true;
    if (Array.isArray(v)) return v.length === 0;
    if (typeof v === 'object') {
        return Object.values(v as Record<string, unknown>).every(
            (x) => x === null || x === undefined || (Array.isArray(x) && !x.length),
        );
    }
    return false;
}

/**
 * Zlucenie zmien do stavu kriterii.
 *
 * Rozsahy (price, usableArea, roomCount) a lokalita su objekty, preto sa zlucuju
 * DO HLBKY. Ked clovek povie "aspon 70 metrov" a potom "najviac 90", model posle
 * len tu druhu hranicu - plytke prepisanie by prvu zahodilo a vysledky by boli zle.
 *
 * null kdekolvek znamena odobranie.
 */
function merge(prev: VoxCriteria, patch: Partial<VoxCriteria>): VoxCriteria {
    const next: Record<string, unknown> = { ...prev };

    Object.keys(patch || {}).forEach((k) => {
        const v = (patch as Record<string, unknown>)[k];
        if (v === null) {
            delete next[k];
            return;
        }
        if (v === undefined) return;

        const stare = next[k];
        const objekt = (x: unknown) => x && typeof x === 'object' && !Array.isArray(x);

        if (objekt(v) && objekt(stare)) {
            const spojene: Record<string, unknown> = { ...(stare as Record<string, unknown>) };
            Object.keys(v as Record<string, unknown>).forEach((kk) => {
                const vv = (v as Record<string, unknown>)[kk];
                if (vv === null) delete spojene[kk];
                else spojene[kk] = vv;
            });
            if (jePrazdne(spojene)) delete next[k];
            else next[k] = spojene;
            return;
        }

        if (jePrazdne(v)) delete next[k];
        else next[k] = v;
    });

    return next as VoxCriteria;
}

export function useVox() {
    const [criteria, setCriteria] = useState<VoxCriteria>({});
    const [messages, setMessages] = useState<VoxMessage[]>([]);
    const [items, setItems] = useState<VoxListing[]>([]);
    const [total, setTotal] = useState<number | null>(null);
    const [relax, setRelax] = useState<VoxRelax[]>([]);
    const [mode, setMode] = useState<VoxMode>('idle');
    const [searching, setSearching] = useState(false);
    const [levels, setLevels] = useState<number[]>(() => new Array(BARS).fill(0));
    const [error, setError] = useState<string | null>(null);
    const [settings, setSettings] = useState<VoxSettings | null>(null);

    /** Nastavenie cita aj animacna slucka, ktora nevidi React state. */
    const cfg = useRef(FALLBACK);

    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const seq = useRef(0);

    // Audio
    const stream = useRef<MediaStream | null>(null);
    const audioCtx = useRef<AudioContext | null>(null);
    const raf = useRef<number | null>(null);
    const recorder = useRef<MediaRecorder | null>(null);
    const chunks = useRef<Blob[]>([]);
    const player = useRef<HTMLAudioElement | null>(null);

    // VAD - drzi sa v refoch, lebo bezi v animacnej slucke mimo Reactu
    const speechSeen = useRef(false);
    const segPeak = useRef(0);
    const segStart = useRef(0);
    const lastLoud = useRef(0);
    /** Odhad hlasitosti ticha v miestnosti. Prah reci sa z neho pocita. */
    const noiseFloor = useRef(0.006);
    /**
     * Okamzita hlasitost 0-1 pre vizualne vrstvy.
     *
     * Zamerne v refe, nie v state: rozhranie ju cita kazdy snimok (getLevel)
     * a keby to bol state, kazde citanie by tahalo prekreslenie celej stranky.
     */
    const levelRef = useRef(0);
    /** Spektrum 0-1 pre vizualne vrstvy. Tiez v refe - cita sa kazdy snimok. */
    const barsRef = useRef<number[]>(new Array(BARS).fill(0));
    /** Odkedy je zvuk nad prahom - kratke rany sa tak odfiltruju. */
    const loudSince = useRef(0);
    const conversing = useRef(false);
    /**
     * Cislo relacie. Spustenie mikrofonu ma v sebe cakanie (povolenie, pozdrav)
     * a clovek moze medzitym stlacit zastavenie. Bez tohto by sa po skonceni
     * cakania rozhovor znova zapol - mikrofon by ostal aktivny napriek zastaveniu.
     */
    const relacia = useRef(0);
    /** Nahravac sa po kazdom segmente restartuje sam - cez ref, aby sa funkcia
     *  neodkazovala na seba este pred svojou deklaraciou. */
    const restart = useRef<() => void>(() => undefined);
    /** say() je definovane nizsie - vyhladavanie ho vola cez ref. */
    const sayRef = useRef<(t: string) => Promise<void>>(async () => undefined);
    const busy = useRef(false);

    // Aktualne kriteria pre slucku, ktora nevidi novy state
    const criteriaRef = useRef<VoxCriteria>({});
    const totalRef = useRef<number | null>(null);
    const messagesRef = useRef<VoxMessage[]>([]);
    useEffect(() => {
        criteriaRef.current = criteria;
    }, [criteria]);
    useEffect(() => {
        messagesRef.current = messages;
    }, [messages]);

    useEffect(() => {
        let alive = true;
        fetch(VOX_API + '/settings')
            .then((r) => r.json())
            .then((s: VoxSettings) => {
                if (!alive) return;
                setSettings(s);
                cfg.current = {
                    silenceMs: s.silenceMs ?? SILENCE_MS,
                    vadLevel: s.vadLevel ?? SPEECH_ON,
                    idleEndMs: s.idleEndMs ?? IDLE_END_MS,
                    greeting: s.greeting || GREETING,
                    startPhrase: s.startPhrase || FALLBACK.startPhrase,
                };
            })
            .catch(() => undefined);
        return () => {
            alive = false;
        };
    }, []);

    /**
     * Uvodne oslovenie po prichode na stranku - raz za nacitanie.
     *
     * Prehliadace blokuju automaticke prehravanie so zvukom, kym clovek na stranke
     * nic neurobi. Preto: skusime to hned, a ked to prehliadac odmietne, pockame na
     * prvy klik, klavesu alebo skrolovanie a povieme to vtedy. Ziadna chyba sa
     * nezobrazuje - je to privitanie, nie funkcia.
     */
    const greeted = useRef(false);
    useEffect(() => {
        if (greeted.current) return;

        let cleanup: (() => void) | null = null;

        /**
         * Uvitanie je NAHRATE modelom, ktory vedie dialog (/hlas/uvitanie.mp3).
         * Bezny /speak pouziva iny model a znel by inak aj pri rovnakom hlase.
         * Keby subor chybal, spadneme spat na /speak - radsej iny hlas nez ticho.
         */
        const zdroj = VOX_API + '/hlas/uvitanie.mp3';
        const zaloha = VOX_API + '/speak?text=' + encodeURIComponent(cfg.current.greeting);

        const play = () => {
            if (greeted.current) return;
            greeted.current = true;
            const a = new Audio(zdroj);
            a.onerror = () => {
                const b = new Audio(zaloha);
                b.play().catch(() => undefined);
            };
            a.play().catch(() => undefined);
            if (cleanup) cleanup();
        };

        // Nastavenie sa nacita chvilu po mountnuti, tak dame promptu cas dojst.
        const t = setTimeout(() => {
            const a = new Audio(zdroj);
            a.onerror = () => {
                const b = new Audio(zaloha);
                b.play().catch(() => undefined);
            };
            a.play()
                .then(() => {
                    greeted.current = true;
                })
                .catch(() => {
                    // Prehliadac autoplay zamietol - pockame na prve gesto.
                    const onGesture = () => play();
                    const events: Array<keyof DocumentEventMap> = ['pointerdown', 'keydown', 'wheel', 'touchstart'];
                    events.forEach((e) => document.addEventListener(e, onGesture, { once: true, passive: true }));
                    cleanup = () => events.forEach((e) => document.removeEventListener(e, onGesture));
                });
        }, 900);

        return () => {
            clearTimeout(t);
            if (cleanup) cleanup();
        };
    }, []);

    const applySettings = useCallback((s: VoxSettings) => {
        setSettings(s);
        cfg.current = {
            silenceMs: s.silenceMs ?? SILENCE_MS,
            vadLevel: s.vadLevel ?? SPEECH_ON,
            idleEndMs: s.idleEndMs ?? IDLE_END_MS,
            greeting: s.greeting || GREETING,
            startPhrase: s.startPhrase || FALLBACK.startPhrase,
        };
    }, []);

    const active = useMemo(
        () => (Object.keys(criteria) as Array<keyof VoxCriteria>).filter((k) => !isEmpty(criteria[k])),
        [criteria],
    );
    const started = active.length > 0 || messages.length > 0 || !!error || mode !== 'idle';

    /* --- vyhladavanie, debounced ------------------------------------- */
    const runSearch = useCallback((c: VoxCriteria) => {
        if (timer.current) clearTimeout(timer.current);
        if (!Object.keys(c).length) {
            setItems([]);
            setTotal(null);
            setRelax([]);
            return;
        }
        timer.current = setTimeout(async () => {
            const my = ++seq.current;
            setSearching(true);
            try {
                const r = await fetch(VOX_API + '/search', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ criteria: c, limit: 12 }),
                });
                const data = await r.json();
                if (my !== seq.current) return;
                setItems(Array.isArray(data.items) ? data.items : []);
                setTotal(typeof data.total === 'number' ? data.total : 0);
                totalRef.current = typeof data.total === 'number' ? data.total : 0;
                setRelax(Array.isArray(data.relax) ? data.relax : []);

                // Nula vysledkov nesmie skoncit tichom. Ked bezi hlasovy rozhovor,
                // Realika to povie a navrhne, ktore kriterium uvolnit - inak clovek
                // ceka a nevie, ze ma povedat novu sumu.
                if (data.total === 0 && conversing.current && !busy.current) {
                    const first = Array.isArray(data.relax) ? data.relax[0] : null;
                    const sentence = first
                        ? `Na toto som nič nenašiel. Pomohlo by ${first.hint} - vtedy mám ${first.count} ponúk. Skúsime to?`
                        : 'Na toto som nič nenašiel. Skúste zmeniť lokalitu alebo typ nehnuteľnosti.';
                    setMessages((m) => [...m, { role: 'assistant', content: sentence }]);
                    busy.current = true;
                    await sayRef.current(sentence);
                    busy.current = false;
                    if (conversing.current) setMode('listening');
                }
            } catch {
                if (my === seq.current) setError('Vyhľadávanie zlyhalo, skús to znova.');
            } finally {
                if (my === seq.current) setSearching(false);
            }
        }, DEBOUNCE_MS);
    }, []);

    /* --- hlas asistenta ------------------------------------------------ */
    /** Prehra vetu a pocka, kym dohovori. Nahravanie je medzitym zastavene. */
    const say = useCallback(async (text: string) => {
        if (!text) return;
        setMode('speaking');
        try {
            await new Promise<void>((resolve) => {
                const a = new Audio(VOX_API + '/speak?text=' + encodeURIComponent(text));
                player.current = a;
                const done = () => resolve();
                a.onended = done;
                a.onerror = done;
                // Zastavenie rozhovoru zvuk pauzne - bez tohto by sa cakalo
                // az na poistku a rozhovor by sa "prebral" o 15 sekund.
                a.onpause = done;
                // Poistka - keby sa zvuk nenacital, dialog nesmie zamrznut.
                setTimeout(done, 15000);
                a.play().catch(done);
            });
        } finally {
            player.current = null;
        }
    }, []);

    /* --- jeden tah rozhovoru ------------------------------------------ */
    /** @returns vetu, ktoru ma VOX povedat nahlas (alebo prazdny retazec) */
    const handleText = useCallback(
        async (raw: string): Promise<string> => {
            const text = String(raw || '').trim();
            if (!text) return '';

            setError(null);
            setMessages((m) => [...m, { role: 'user', content: text }]);
            setMode('thinking');

            try {
                const r = await fetch(VOX_API + '/extract', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        text,
                        criteria: criteriaRef.current,
                        history: messagesRef.current.filter((m) => m.role !== 'system').slice(-8),
                        // Aby vedel odpovedat na "kolko ich je" bez toho, aby to hadal.
                        total: totalRef.current,
                    }),
                });
                if (!r.ok) throw new Error('extract');
                const data = await r.json();

                const patch: Partial<VoxCriteria> = { ...(data.patch || {}), ...(data.unsupported || {}) };
                const changed = Object.keys(patch).length > 0;

                // "Ideme na nove hladanie" - vsetky kriteria sa zahodia. Ked v tej
                // istej vete zaznelo aj nieco nove, pouzije sa to na prazdny stav.
                const base: VoxCriteria = data.reset ? {} : criteriaRef.current;
                if (data.reset) {
                    seq.current++; // zahod odpoved na predchadzajuce hladanie
                    setItems([]);
                    setTotal(null);
                    setRelax([]);
                    setMessages((m) => [...m, { role: 'system', content: 'Kritériá zmazané - ideme odznova.' }]);
                }

                const next = merge(base, patch);

                criteriaRef.current = next;
                setCriteria(next);
                runSearch(next);

                (Array.isArray(data.derived) ? data.derived : []).forEach((d: string) =>
                    setMessages((m) => [...m, { role: 'system', content: d }]),
                );

                let reply = '';
                // Prazdny patch NIE JE nedorozumenie. Ked sa nic nezmenilo, ale model
                // ma co povedat (odpoved na otazku, potvrdenie), pouzije sa reply.
                // Vetu o nerozumeni hovorime az ked nema ani jedno.
                if (data.reply && !data.question) reply = String(data.reply);
                else if (data.reset && !changed)
                    reply = data.question
                        ? 'Dobre, ideme odznova. ' + String(data.question)
                        : 'Dobre, ideme odznova. Čo hľadáte?';
                else if (data.question) reply = String(data.question);
                else if (!changed && !data.reply)
                    reply = 'Tomuto som nerozumel. Skúste povedať, kde hľadáte, aký typ a približný rozpočet.';

                if (reply) setMessages((m) => [...m, { role: 'assistant', content: reply }]);
                return reply;
            } catch {
                setError('Nepodarilo sa spracovať vstup. Skús to prosím znova.');
                return '';
            }
        },
        [runSearch],
    );

    /* --- nahravanie a krajenie na vety --------------------------------- */
    const startRecorder = useCallback(() => {
        const s = stream.current;
        if (!s || !conversing.current) return;
        try {
            // Vyslovny format - bez neho vracia mimeType prazdny retazec a server
            // potom hada priponu, co OpenAI odmieta ako poskodeny subor.
            const types = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];
            const supported = types.find((t) => typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported?.(t));
            const mr = supported ? new MediaRecorder(s, { mimeType: supported }) : new MediaRecorder(s);
            recorder.current = mr;
            chunks.current = [];
            speechSeen.current = false;
            loudSince.current = 0;
            segPeak.current = 0;
            segStart.current = Date.now();
            lastLoud.current = Date.now();

            mr.ondataavailable = (e) => {
                if (e.data.size) chunks.current.push(e.data);
            };

            mr.onstop = async () => {
                const blob = new Blob(chunks.current, { type: mr.mimeType || 'audio/webm' });
                const durationMs = Date.now() - segStart.current;
                const peak = segPeak.current;
                const hadSpeech = speechSeen.current;

                if (!conversing.current) return;

                // Ticho ani cvrlikanie k modelu neposielame.
                if (!hadSpeech || blob.size < 1500 || durationMs < MIN_SEGMENT_MS || peak < Math.max(cfg.current.vadLevel, noiseFloor.current * 6)) {
                    vlog('segment-zahodeny', {
                        hadSpeech,
                        size: blob.size,
                        durationMs,
                        peak: Number(peak.toFixed(3)),
                        prah: cfg.current.vadLevel,
                    });
                    restart.current();
                    return;
                }

                busy.current = true;
                setMode('thinking');
                try {
                    const fd = new FormData();
                    fd.append('audio', blob, 'speech.webm');
                    fd.append('durationMs', String(durationMs));
                    fd.append('peak', peak.toFixed(3));
                    vlog('posielam-na-prepis', { size: blob.size, durationMs, peak: Number(peak.toFixed(3)) });
                    const r = await fetch(VOX_API + '/stt', { method: 'POST', body: fd });
                    const data = await r.json();
                    vlog('prepis-hotovy', { text: data.text || '', discarded: !!data.discarded, reason: data.reason || '' });

                    if (data.text) {
                        const reply = await handleText(data.text);
                        if (reply && conversing.current) await say(reply);
                    } else if (data.discarded) {
                        setMessages((m) => [
                            ...m,
                            { role: 'system', content: 'Rozumel som len šumu - radšej nič, než vymyslená veta.' },
                        ]);
                    }
                } catch {
                    setError('Prepis zlyhal. Skús to prosím znova.');
                } finally {
                    busy.current = false;
                    if (conversing.current) {
                        setMode('listening');
                        restart.current();
                    }
                }
            };

            mr.start();
            vlog('nahravam', { mime: mr.mimeType, vadLevel: cfg.current.vadLevel, silenceMs: cfg.current.silenceMs });
            setMode('listening');
        } catch {
            setError('Nahrávanie sa nepodarilo spustiť.');
        }
    }, [handleText, say]);

    useEffect(() => {
        restart.current = startRecorder;
    }, [startRecorder]);

    useEffect(() => {
        sayRef.current = say;
    }, [say]);

    /** Meranie hlasitosti: pohana siluetu aj rozpoznanie konca vety. */
    const startMeter = useCallback(
        (s: MediaStream) => {
            try {
                const Ctx =
                    window.AudioContext ||
                    (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
                const ctx = new Ctx();
                audioCtx.current = ctx;
                const analyser = ctx.createAnalyser();
                analyser.fftSize = 256;
                analyser.smoothingTimeConstant = 0.75;
                ctx.createMediaStreamSource(s).connect(analyser);
                const data = new Uint8Array(analyser.frequencyBinCount);
                // Casova oblast pre VAD. Frekvencne hodnoty su pre siluetu umelo
                // zosilnene (pow*1.6), takze ticho v nich vyzera ako 0.4 - na
                // rozhodovanie o konci vety sa nedaju pouzit. Toto je cista
                // efektivna hodnota amplitudy: ticho ~0.005, rec 0.05 az 0.3.
                const wave = new Uint8Array(analyser.fftSize);

                let last = 0;
                let lastReport = 0;
                const tick = (now?: number) => {
                    const t = now ?? 0;
                    raf.current = requestAnimationFrame(tick);
                    if (t - last < 33) return;
                    last = t;

                    analyser.getByteFrequencyData(data);
                    const usable = Math.floor(data.length * 0.6);
                    const per = Math.max(1, Math.floor(usable / BARS));
                    const next: number[] = [];
                    for (let i = 0; i < BARS; i++) {
                        let sum = 0;
                        for (let j = 0; j < per; j++) sum += data[i * per + j] || 0;
                        const v = sum / per / 255;
                        next.push(Math.min(1, Math.pow(v, 0.75) * 1.6));
                    }
                    setLevels(next);
                    barsRef.current = next;

                    // Efektivna hlasitost (RMS) pre rozhodovanie o reci a tichu.
                    analyser.getByteTimeDomainData(wave);
                    let acc = 0;
                    for (let i = 0; i < wave.length; i++) {
                        const d = (wave[i] - 128) / 128;
                        acc += d * d;
                    }
                    const rms = Math.sqrt(acc / wave.length);
                    // Skalovanie na 0-1 pre vizual: rec sa u beznych mikrofonov
                    // pohybuje okolo 0.05-0.3, cize by surove rms bolo takmer neviditelne.
                    levelRef.current = Math.min(1, rms * 4);

                    if (t - lastReport > 2000) {
                        lastReport = t;
                        vlog('hlasitost', {
                            rms: Number(rms.toFixed(4)),
                            prah: Number(Math.max(cfg.current.vadLevel, noiseFloor.current * 6).toFixed(4)),
                            sum: Number(noiseFloor.current.toFixed(4)),
                            nastavenyPrah: cfg.current.vadLevel,
                            silueta: Number(next.reduce((m, v) => (v > m ? v : m), 0).toFixed(2)),
                            recState: recorder.current?.state || 'ziadny',
                            busy: busy.current,
                        });
                    }
                    const rec = recorder.current;
                    const nowMs = Date.now();

                    // VAD bezi len ked naozaj nahravame a VOX prave nehovori.
                    if (!rec || rec.state !== 'recording' || busy.current) return;

                    if (rms > segPeak.current) segPeak.current = rms;

                    // Prah sa prisposobuje miestnosti a mikrofonu. Fixne cislo nefunguje:
                    // tichy mikrofon da rec na 0.08, hlasny na 0.4 - a to iste cislo je
                    // pre jeden ticho a pre druhy rec. Sumova podlaha klesa rychlo,
                    // stupa pomaly, aby ju jedno hlasne slovo nezdvihlo.
                    if (rms < noiseFloor.current) noiseFloor.current = noiseFloor.current * 0.9 + rms * 0.1;
                    else noiseFloor.current = noiseFloor.current * 0.999 + rms * 0.001;
                    // vadLevel z nastavenia je teraz SPODNA hranica, nie strop - takze
                    // posuvnik "citlivost" v paneli priamo urcuje, ako hlasny musi byt
                    // zvuk, aby sa vobec bral. Nasobok sumu 6x drzi prah nad hlukom
                    // miestnosti aj vtedy, ked je ticho velmi ticho.
                    const threshold = Math.max(cfg.current.vadLevel, noiseFloor.current * 6);

                    if (rms >= threshold) {
                        // Jedno klepnutie do klavesnice, buchnutie dvermi alebo kliknutie
                        // mysou prekroci prah na jeden-dva snimky. Rec musi byt nad prahom
                        // aspon SPEECH_MIN_MS spojite, inak sa nepocita.
                        if (loudSince.current === 0) loudSince.current = nowMs;
                        lastLoud.current = nowMs;
                        if (!speechSeen.current && nowMs - loudSince.current >= SPEECH_MIN_MS) {
                            speechSeen.current = true;
                            vlog('rec-zachytena', { rms: Number(rms.toFixed(4)), prah: Number(threshold.toFixed(4)) });
                        }
                        return;
                    }
                    loudSince.current = 0;

                    const quietFor = nowMs - lastLoud.current;

                    // Poistka: aj keby sa ticho nikdy nerozpoznalo (prilis hlasne
                    // okolie, zle nastaveny prah), segment sa po 12 s uzavrie sam
                    // a posle na prepis - inak by rozhovor mlcky zamrzol.
                    if (speechSeen.current && nowMs - segStart.current > 12000) {
                        vlog('poistka-12s', { peak: Number(segPeak.current.toFixed(4)) });
                        rec.stop();
                        return;
                    }

                    // Veta dokoncena - uzavri segment a posli na prepis.
                    if (speechSeen.current && quietFor > cfg.current.silenceMs) {
                        vlog('koniec-vety', { quietFor, peak: Number(segPeak.current.toFixed(3)) });
                        rec.stop();
                        return;
                    }

                    // Clovek dlho nepovedal nic - rozhovor konci sam.
                    if (!speechSeen.current && quietFor > cfg.current.idleEndMs) {
                        conversing.current = false;
                        rec.stop();
                        setMessages((m) => [
                            ...m,
                            { role: 'system', content: 'Rozhovor ukončený - dlho som nič nepočul.' },
                        ]);
                        setMode('idle');
                        s.getTracks().forEach((t) => t.stop());
                    }
                };
                raf.current = requestAnimationFrame(tick);
            } catch {
                /* silueta je ozdoba, dialog bezi dalej */
            }
        },
        [],
    );

    /* --- ovladanie rozhovoru ------------------------------------------- */
    /** Okamzita hlasitost. Nesmie alokovat ani prekreslovat - preto len citanie refu. */
    const getLevel = useCallback(() => levelRef.current, []);

    /** Spektrum pre vlnu. Rovnako ako getLevel - len citanie refu, ziadne prekreslenie. */
    const getBars = useCallback(() => barsRef.current, []);

    const stop = useCallback(() => {
        relacia.current++;
        conversing.current = false;
        if (player.current) {
            player.current.pause();
            player.current = null;
        }
        if (recorder.current && recorder.current.state !== 'inactive') recorder.current.stop();
        if (raf.current !== null) {
            cancelAnimationFrame(raf.current);
            raf.current = null;
        }
        if (audioCtx.current) {
            audioCtx.current.close().catch(() => undefined);
            audioCtx.current = null;
        }
        if (stream.current) {
            stream.current.getTracks().forEach((t) => t.stop());
            stream.current = null;
        }
        setLevels(new Array(BARS).fill(0));
        levelRef.current = 0;
        barsRef.current = new Array(BARS).fill(0);
        setMode('idle');
    }, []);

    const start = useCallback(async () => {
        if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
            setError('Tento prehliadač nepodporuje nahrávanie. Napíš to prosím do poľa - funguje to rovnako.');
            return;
        }
        const moja = ++relacia.current;
        try {
            // Stream sa otvara RAZ na cely rozhovor.
            const s = await navigator.mediaDevices.getUserMedia({
                audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
            });

            // Clovek medzitym stlacil zastavenie - mikrofon hned zavrieme
            if (moja !== relacia.current) {
                s.getTracks().forEach((t) => t.stop());
                return;
            }

            stream.current = s;
            conversing.current = true;
            vlog('mic-povoleny', { tracks: s.getAudioTracks().length });
            busy.current = false;
            setError(null);
            startMeter(s);

            // Pozdrav. Nahravanie zacne az ked dohovori, aby si sa nepocul sam.
            // Uvodne oslovenie znie po prichode na stranku. Tu uz len kratka veta,
            // aby sa rozhovor nezacinal dlhym privitanim po kazdom kliknuti.
            busy.current = true;
            setMessages((m) => [...m, { role: 'assistant', content: cfg.current.startPhrase }]);
            await say(cfg.current.startPhrase);
            busy.current = false;

            if (moja === relacia.current && conversing.current) startRecorder();
        } catch (e) {
            const name = (e as { name?: string })?.name || '';
            if (name === 'NotAllowedError')
                setError(
                    'Prehliadač prístup k mikrofónu nepovolil. Skontroluj povolenie pre túto stránku - alebo to napíš do poľa.',
                );
            else if (name === 'NotFoundError' || name === 'DevicesNotFoundError')
                setError('Nenašiel som žiadny mikrofón. Napíš to prosím do poľa.');
            else if (name === 'NotReadableError')
                setError('Mikrofón je obsadený inou aplikáciou. Zavri ju a skús znova.');
            else setError('Mikrofón sa nepodarilo spustiť. Napíš to do poľa - funguje to rovnako.');
            conversing.current = false;
            setMode('idle');
        }
    }, [say, startMeter, startRecorder]);

    const toggleMic = useCallback(() => {
        if (mode === 'idle') void start();
        else stop();
    }, [mode, start, stop]);

    /** Textovy vstup - zaloha, funguje aj bez mikrofonu a aj pocas rozhovoru. */
    const ask = useCallback(
        async (raw: string) => {
            const reply = await handleText(raw);
            if (conversing.current) {
                if (reply) await say(reply);
                setMode('listening');
            } else {
                setMode('idle');
            }
        },
        [handleText, say],
    );

    /* --- rucna oprava kriterii ----------------------------------------- */
    const removeCriterion = useCallback(
        (key: keyof VoxCriteria) => {
            const next = { ...criteriaRef.current };
            delete next[key];
            criteriaRef.current = next;
            setCriteria(next);
            runSearch(next);
        },
        [runSearch],
    );

    const applyRelax = useCallback(
        (r: VoxRelax) => {
            const next: VoxCriteria = { ...criteriaRef.current };
            r.keys.forEach((k) => {
                // Cena a okruh sa uvolnuju posunutim hranice, ostatne kriteria sa vypustaju
                if (k === 'price' && next.price?.max) {
                    next.price = { ...next.price, max: Math.round(next.price.max * 1.15) };
                } else if (k === 'usableArea' && next.usableArea?.min) {
                    next.usableArea = { ...next.usableArea, min: Math.round(next.usableArea.min * 0.85) };
                } else if (k === 'location' && next.location?.radiusKm) {
                    next.location = { ...next.location, radiusKm: next.location.radiusKm + 25 };
                } else {
                    delete next[k as keyof VoxCriteria];
                }
            });
            criteriaRef.current = next;
            setCriteria(next);
            runSearch(next);
            setMessages((m) => [...m, { role: 'system', content: 'Uvoľnené: ' + r.label }]);
        },
        [runSearch],
    );

    const reset = useCallback(() => {
        stop();
        if (timer.current) clearTimeout(timer.current);
        seq.current++;
        criteriaRef.current = {};
        messagesRef.current = [];
        setCriteria({});
        setMessages([]);
        setItems([]);
        setTotal(null);
        setRelax([]);
        setError(null);
    }, [stop]);

    useEffect(() => {
        return () => {
            if (timer.current) clearTimeout(timer.current);
            if (raf.current !== null) cancelAnimationFrame(raf.current);
            if (audioCtx.current) audioCtx.current.close().catch(() => undefined);
            if (stream.current) stream.current.getTracks().forEach((t) => t.stop());
        };
    }, []);

    return {
        criteria,
        activeKeys: active,
        messages,
        items,
        total,
        relax,
        mode,
        listening: mode !== 'idle',
        thinking: mode === 'thinking',
        speaking: mode === 'speaking',
        searching,
        levels,
        getLevel,
        getBars,
        error,
        started,
        settings,
        applySettings,
        ask,
        removeCriterion,
        applyRelax,
        reset,
        toggleMic,
        stop,
    };
}

export type VoxState = ReturnType<typeof useVox>;
